import { PortalDepositTableProps } from '@/components/portal-modules/PortalDepositTable';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { convertToTableRow, portalDepositColumns } from '@/helpers/portalDepositHelper';
import { useAppSelector } from '@/state/hooks';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { DepositWithdrawalRequestHistory } from '@/types/sso';
import {
    ColumnDef,
    filterFns,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

export interface PortalDepositRow extends DepositWithdrawalRequestHistory {
    tableUpdatedAt: string;
    tableAmount: string;
    tableMethod: string;
    tableStatus: string;
}

interface UsePortalDepositTableProps extends PortalDepositTableProps {}

const usePortalDepositTable = (props: UsePortalDepositTableProps) => {
    const { data, setOffset } = props;

    const pagination = useAppSelector(selectTablePagination);

    const formatQty = useNumberFormatter(convertedQtyFormatterConfig);
    const formatDate = useDateFormatter();

    const lang = useLanguage();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

    const columns: ColumnDef<PortalDepositRow>[] = useMemo(() => {
        return portalDepositColumns({ lang, formatQty, formatDate });
    }, [lang, formatQty, formatDate]);

    const formattedPortalDeposits: PortalDepositRow[] = useMemo(() => {
        if (data) return data.data.map((request) => convertToTableRow(request, formatQty, formatDate));
        return [];
    }, [data]);

    const tableInstance = useReactTable({
        data: formattedPortalDeposits,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        pageCount: Math.ceil((data?.total || 0) / pagination.pageSize),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, formatDate);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        enableRowSelection: true,
        manualPagination: true
    });

    useEffect(() => {
        const pageIndex = tableInstance.getState().pagination.pageIndex;
        setOffset(pageIndex * pagination.pageSize);
    }, [tableInstance.getState().pagination.pageIndex]);

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalDeposits, globalFilter };
};

export default usePortalDepositTable;
